import type { RefObject } from 'react'
import type { Chart } from 'klinecharts'
import { captureWorkbench } from './snapshot'
import { defaultBackground } from './chartStyles'
import { useIsDark } from './useIsDark'

/**
 * Chart snapshot actions, shared by the desktop camera menu and the mobile
 * overflow menu. `save` downloads a PNG; `copy` puts it on the clipboard and
 * reports success (clipboard access can be blocked by the browser).
 */
export function useWorkbenchSnapshot(chart: Chart | null, captureRef: RefObject<HTMLDivElement | null>) {
  const isDark = useIsDark()
  const bg = defaultBackground(isDark)

  // PNG data URL of the WHOLE chart area: canvas (candles/indicators/drawings/volume)
  // plus the HTML overlays (status line, legends). Falls back to the canvas-only grab
  // if the composite fails for any reason.
  const capture = async (): Promise<string | null> => {
    const el = captureRef.current
    if (el) {
      try {
        const url = await captureWorkbench(el, bg)
        if (url) return url
      } catch {
        /* fall through to canvas-only */
      }
    }
    return chart ? chart.getConvertPictureUrl(true, 'png', bg) : null
  }

  const save = async () => {
    const url = await capture()
    if (url) {
      const a = document.createElement('a')
      a.href = url
      a.download = `chart-${Date.now()}.png`
      a.click()
    }
  }

  const copy = async (): Promise<boolean> => {
    const url = await capture()
    if (!url) return false
    try {
      const blob = await (await fetch(url)).blob()
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      return true
    } catch {
      // clipboard can be blocked by the browser; the save path still works
      return false
    }
  }

  return { save, copy }
}
